import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import PinchZoomImage from './PinchZoomImage';
import {
  ArrowLeft, Dumbbell, Scale, Clock, Trophy, Flame,
  Target, Activity, Calendar, User, Loader2, MapPin,
  Zap, Heart, TrendingUp, Apple, ChevronDown, ChevronUp, Ruler
} from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { SocialPost, FeedFilterBar, filterPosts } from '../pages/SocialPage';
import client from '../api/client';
import { toast } from 'sonner';
import { TARGET_AREA_I18N_KEYS as TARGET_AREA_KEYS } from '../constants/profileLabels';

const GOAL_KEYS = {
  weight_loss: 'userProfile.goal.weight_loss', muscle_gain: 'userProfile.goal.muscle_gain',
  strength: 'userProfile.goal.strength', endurance: 'userProfile.goal.endurance',
  flexibility: 'userProfile.goal.flexibility', general_fitness: 'userProfile.goal.general_fitness',
  lose_weight: 'userProfile.goal.lose_weight', maintain_weight: 'userProfile.goal.maintain_weight',
  gain_weight: 'userProfile.goal.gain_weight',
};
const EXPERIENCE_KEYS = {
  beginner: 'userProfile.experience.beginner', intermediate: 'userProfile.experience.intermediate',
  advanced: 'userProfile.experience.advanced',
};
const EQUIPMENT_KEYS = {
  full_gym: 'userProfile.equipment.full_gym', home_basic: 'userProfile.equipment.home_basic',
  bodyweight_only: 'userProfile.equipment.bodyweight_only',
};
const ACTIVITY_KEYS = {
  sedentary: 'userProfile.activity.sedentary', lightly_active: 'userProfile.activity.lightly_active',
  moderately_active: 'userProfile.activity.moderately_active', very_active: 'userProfile.activity.very_active',
  extremely_active: 'userProfile.activity.extremely_active',
};
function getRoleBadge(role) {
  const styles = {
    admin: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
    coach: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    client: 'bg-green-500/10 text-green-500 border-green-500/30',
  };
  return styles[role] || styles.client;
}

function InfoTag({ icon: Icon, label, value, color = '#FF5C00' }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 min-w-0">
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color }} />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground leading-tight">{label}</p>
        <p className="text-xs font-semibold truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/30 min-w-0">
      <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function UserProfileView({ userDetails, onBack, actionButtons, children }) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const pu = userDetails.user;
  const { recent_measurements = [], stats } = userDetails;

  // Feed state
  const [userFeed, setUserFeed] = useState([]);
  const [feedPage, setFeedPage] = useState(1);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedFilter, setFeedFilter] = useState('all');
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] });
  const [friendIds, setFriendIds] = useState(new Set());

  const fetchFeed = useCallback(async (pageNum = 1, append = false) => {
    setFeedLoading(true);
    try {
      const res = await client.get(`/social/user-feed/${pu.id}`, { params: { page: pageNum, limit: 10 } });
      if (append) setUserFeed(prev => [...prev, ...res.data.posts]);
      else setUserFeed(res.data.posts);
      setFeedHasMore(res.data.has_more);
      setFeedPage(pageNum);
    } catch (err) { toast.error(err?.response?.data?.detail || t('userProfile.errorLoadFeed')); }
    finally { setFeedLoading(false); }
  }, [pu.id, t]);

  const fetchFriendData = useCallback(async () => {
    try {
      const [reqRes, friendsRes] = await Promise.all([
        client.get('/friend-requests'),
        client.get('/friends')
      ]);
      setFriendRequests(reqRes.data);
      setFriendIds(new Set(friendsRes.data.map(f => f.id)));
    } catch (err) { toast.error(err?.response?.data?.detail || t('userProfile.errorLoadFriends')); }
  }, [t]);

  useEffect(() => { fetchFeed(); fetchFriendData(); }, [fetchFeed, fetchFriendData]);

  const handleLike = async (postId) => {
    try {
      const res = await client.post(`/social/posts/${postId}/like`);
      setUserFeed(prev => prev.map(p => p.id === postId ? { ...p, likes: res.data.likes } : p));
    } catch { toast.error(t('userProfile.errorLike')); }
  };
  const handleComment = async (postId, text) => {
    if (!text.trim()) return;
    try {
      const res = await client.post(`/social/posts/${postId}/comment`, { text });
      setUserFeed(prev => prev.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), res.data.comment] } : p));
    } catch { toast.error(t('userProfile.errorComment')); }
  };
  const handleDeleteComment = async (postId, commentId) => {
    try {
      await client.delete(`/social/posts/${postId}/comment/${commentId}`);
      setUserFeed(prev => prev.map(p => p.id === postId ? { ...p, comments: (p.comments || []).filter(c => c.id !== commentId) } : p));
    } catch (err) { toast.error(err?.response?.data?.detail || t('userProfile.errorDeleteComment')); }
  };
  const handleDeletePost = async (postId) => {
    try {
      await client.delete(`/social/posts/${postId}`);
      setUserFeed(prev => prev.filter(p => p.id !== postId));
    } catch (err) { toast.error(err?.response?.data?.detail || t('userProfile.errorDeletePost')); }
  };
  const handleFriendRequest = async (uid) => {
    try {
      const existing = friendRequests.outgoing.find(r => r.to_user_id === uid);
      if (existing) {
        await client.delete(`/friend-requests/${existing.id}`);
      } else {
        await client.post('/friend-requests', { to_user_id: uid });
      }
      fetchFriendData();
    } catch (err) { toast.error(err?.response?.data?.detail || t('userProfile.errorFriendRequest')); }
  };

  // Computed
  const latestWeight = recent_measurements.length > 0 ? recent_measurements[0].weight_kg : null;
  const memberSince = pu.created_at ? format(new Date(pu.created_at), 'MMM yyyy') : null;
  const hasProfile = pu.gender || pu.height_cm || pu.birth_date || pu.objective || pu.activity_level || pu.experience_level || pu.workout_frequency || pu.preferred_workout_duration || pu.equipment_access || (pu.target_areas && pu.target_areas.length > 0);

  return (
    <div className="space-y-4 pb-20 md:pb-0" data-testid="user-profile-view">

      {/* ═══ PROFILE HEADER ═══ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-muted/80 to-muted/30 border border-border/50">
        {/* Orange accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/40" />

        <div className="p-4 sm:p-6">
          {/* Back + Actions row */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn" className="h-11 w-11 md:h-8 md:w-8 shrink-0" tooltip={t('tooltips.back')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {actionButtons && (
              <div className="flex flex-wrap gap-1.5 justify-end">
                {actionButtons}
              </div>
            )}
          </div>

          {/* Avatar + Name + Meta */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <Avatar className="h-20 w-20 sm:h-24 sm:w-24 border-3 border-primary/30 shrink-0 ring-2 ring-background">
              {pu.profile_picture ? <AvatarImage src={pu.profile_picture} alt={pu.name} /> : null}
              <AvatarFallback className="bg-primary/10 text-primary font-bold text-2xl sm:text-3xl">
                {pu.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {pu.name?.toUpperCase()}
              </h1>
              <p className="text-sm text-muted-foreground truncate">{pu.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-1.5 justify-center sm:justify-start">
                <Badge variant="outline" className={`${getRoleBadge(pu.role)} text-xs`}>{pu.role}</Badge>
                {pu.coach_name && <span className="text-xs text-muted-foreground">{t('userProfile.coachLabel')} <span className="text-primary font-medium">{pu.coach_name}</span></span>}
                {memberSince && <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />{t('userProfile.memberSince', { date: memberSince })}</span>}
              </div>
              {pu.notes && (
                <p className="text-sm text-muted-foreground mt-2 italic leading-snug line-clamp-2">"{pu.notes}"</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ QUICK STATS BAR ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <StatCard icon={Dumbbell} value={pu.routine_name || t('userProfile.statNone')} label={t('userProfile.statRoutine')} color="#FF5C00" />
        <StatCard icon={Flame} value={stats?.workouts_this_week || 0} label={t('userProfile.statThisWeek')} color="#22C55E" />
        <StatCard icon={Trophy} value={stats?.total_workouts || 0} label={t('userProfile.statTotalWorkouts')} color="#3B82F6" />
        <StatCard icon={TrendingUp} value={stats?.total_prs || 0} label={t('userProfile.statTotalPRs')} color="#EAB308" />
        <StatCard icon={Scale} value={latestWeight ? `${latestWeight} kg` : t('userProfile.statNA')} label={t('userProfile.statLatestWeight')} color="#A855F7" />
      </div>

      {/* ═══ PROFILE & PREFERENCES (compact merged card) ═══ */}
      {hasProfile && (
        <Card className="border-border/50 overflow-hidden">
          <div className="px-4 pt-4 pb-1">
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <User className="inline h-4 w-4 mr-1.5 text-primary" />{t('userProfile.sectionProfilePreferences')}
            </h3>
          </div>
          <CardContent className="pt-2 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {pu.gender && <InfoTag icon={User} label={t('userProfile.infoGender')} value={pu.gender.charAt(0).toUpperCase() + pu.gender.slice(1)} color="#3B82F6" />}
              {pu.birth_date && <InfoTag icon={Calendar} label={t('userProfile.infoBirthDate')} value={pu.birth_date} color="#3B82F6" />}
              {pu.height_cm && <InfoTag icon={Scale} label={t('userProfile.infoHeight')} value={`${pu.height_cm} cm`} color="#22C55E" />}
              {latestWeight && <InfoTag icon={Scale} label={t('userProfile.infoWeight')} value={`${latestWeight} kg`} color="#FF5C00" />}
              {pu.objective && <InfoTag icon={Target} label={t('userProfile.infoGoal')} value={GOAL_KEYS[pu.objective] ? t(GOAL_KEYS[pu.objective]) : pu.objective} color="#EF4444" />}
              {pu.activity_level && <InfoTag icon={Zap} label={t('userProfile.infoActivity')} value={ACTIVITY_KEYS[pu.activity_level] ? t(ACTIVITY_KEYS[pu.activity_level]) : pu.activity_level} color="#FF5C00" />}
              {pu.experience_level && <InfoTag icon={Dumbbell} label={t('userProfile.infoExperience')} value={EXPERIENCE_KEYS[pu.experience_level] ? t(EXPERIENCE_KEYS[pu.experience_level]) : pu.experience_level} color="#6366F1" />}
              {pu.equipment_access && <InfoTag icon={MapPin} label={t('userProfile.infoEquipment')} value={EQUIPMENT_KEYS[pu.equipment_access] ? t(EQUIPMENT_KEYS[pu.equipment_access]) : pu.equipment_access} color="#8B5CF6" />}
              {pu.workout_frequency && <InfoTag icon={Calendar} label={t('userProfile.infoFrequency')} value={t('userProfile.infoFrequencyValue', { count: pu.workout_frequency })} color="#FF5C00" />}
              {pu.preferred_workout_duration && <InfoTag icon={Clock} label={t('userProfile.infoDuration')} value={t('userProfile.infoDurationValue', { count: pu.preferred_workout_duration })} color="#FF5C00" />}
            </div>

            {/* Target areas as pills */}
            {pu.target_areas && pu.target_areas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold self-center mr-1">{t('userProfile.targets')}</span>
                {pu.target_areas.map(area => (
                  <Badge key={area} className="bg-primary text-white border-none text-xs px-2 py-0.5">
                    {TARGET_AREA_KEYS[area] ? t(TARGET_AREA_KEYS[area]) : area}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ CHILDREN SLOT (Routine Editor, Coach Notes, etc.) ═══ */}
      {children}

      {/* ═══ ACTIVITY FEED ═══ */}
      <div className="space-y-3">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Activity className="inline h-5 w-5 mr-2 text-primary" />{t('userProfile.sectionActivityFeed')}
        </h2>
        <FeedFilterBar filterType={feedFilter} setFilterType={setFeedFilter} />
        {filterPosts(userFeed, feedFilter).length === 0 && !feedLoading ? (
          <Card className="border-border/50">
            <CardContent className="py-10 text-center">
              <Activity className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">{feedFilter === 'all' ? t('userProfile.feedEmpty') : t('userProfile.feedEmptyFilter', { filter: feedFilter })}</p>
            </CardContent>
          </Card>
        ) : (
          filterPosts(userFeed, feedFilter).map(post => (
            <SocialPost
              key={post.id}
              post={post}
              currentUser={currentUser}
              onLike={handleLike}
              onComment={handleComment}
              onDeleteComment={handleDeleteComment}
              onDeletePost={handleDeletePost}
              onSendFriendRequest={handleFriendRequest}
              onPostUpdated={(updated) => setUserFeed(prev => prev.map(p => p.id === updated.id ? updated : p))}
              navigate={navigate}
              friendRequests={friendRequests}
              friendIds={friendIds}
            />
          ))
        )}
        {feedHasMore && (
          <div className="text-center py-2">
            <Button variant="outline" size="sm" onClick={() => fetchFeed(feedPage + 1, true)} disabled={feedLoading}>
              {feedLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('userProfile.loading')}</> : t('userProfile.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


const MEASUREMENT_KEYS = {
  chest_cm: 'userProfile.measurement.chest_cm', waist_cm: 'userProfile.measurement.waist_cm',
  hips_cm: 'userProfile.measurement.hips_cm', shoulders_cm: 'userProfile.measurement.shoulders_cm',
  neck_cm: 'userProfile.measurement.neck_cm', left_arm_cm: 'userProfile.measurement.left_arm_cm',
  right_arm_cm: 'userProfile.measurement.right_arm_cm', left_thigh_cm: 'userProfile.measurement.left_thigh_cm',
  right_thigh_cm: 'userProfile.measurement.right_thigh_cm', left_calf_cm: 'userProfile.measurement.left_calf_cm',
  right_calf_cm: 'userProfile.measurement.right_calf_cm',
};

export function RecentsSection({ recent_workouts, recent_food_logs, recent_measurements }) {
  const { t } = useTranslation();
  const [showAllWorkouts, setShowAllWorkouts] = useState(false);
  const [showAllDiet, setShowAllDiet] = useState(false);
  const [showAllMeasurements, setShowAllMeasurements] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [selectedDiet, setSelectedDiet] = useState(null);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);

  const workoutsToShow = showAllWorkouts ? recent_workouts : recent_workouts.slice(0, 3);
  const dietToShow = showAllDiet ? recent_food_logs : recent_food_logs.slice(0, 3);
  const measurementsToShow = showAllMeasurements ? recent_measurements : recent_measurements.slice(0, 3);

  return (
    <>
      <Card className="border-border/50">
        <CardHeader><CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('userProfile.sectionRecentWorkouts')}</CardTitle></CardHeader>
        <CardContent>
          {recent_workouts.length === 0 ? <p className="text-muted-foreground text-center py-6">{t('userProfile.noWorkouts')}</p> : (
            <div className="space-y-2">
              {workoutsToShow.map((w) => (
                <div key={w.id} onClick={() => setSelectedWorkout(w)} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors" data-testid={`recent-workout-${w.id}`}>
                  <div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-primary/10"><Dumbbell className="h-4 w-4 text-primary" /></div><div><p className="font-medium text-sm">{w.day_name}</p><p className="text-xs text-muted-foreground">{format(new Date(w.completed_at), 'MMM d, yyyy')}</p></div></div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" /> {w.duration_minutes}min</span>
                    {w.prs_achieved > 0 && (() => {
                    const types = [...new Set(w.exercises?.flatMap(ex => ex.sets?.flatMap(s => s.pr_types || (s.is_pr ? ['weight'] : [])) || []) || [])];
                    const colorMap = { weight: 'text-yellow-500', reps: 'text-green-500', e1rm: 'text-purple-500' };
                    return types.length > 0 ? (
                      <div className="flex gap-1">
                        {types.map(type => <Trophy key={type} className={`h-3 w-3 ${colorMap[type] || 'text-yellow-500'}`} />)}
                      </div>
                    ) : (
                      <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30"><Trophy className="h-3 w-3 mr-1" /> {w.prs_achieved} PR</Badge>
                    );
                  })()}
                  </div>
                </div>
              ))}
              {recent_workouts.length > 3 && (
                <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setShowAllWorkouts(!showAllWorkouts)}>
                  {showAllWorkouts ? <><ChevronUp className="h-4 w-4 mr-1" />{t('userProfile.showLess')}</> : <><ChevronDown className="h-4 w-4 mr-1" />{t('userProfile.showAll', { count: recent_workouts.length })}</>}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/50">
        <CardHeader><CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('userProfile.sectionRecentDiet')}</CardTitle></CardHeader>
        <CardContent>
          {recent_food_logs.length === 0 ? <p className="text-muted-foreground text-center py-6">{t('userProfile.noDiet')}</p> : (
            <div className="space-y-2">
              {dietToShow.map((log) => (
                <div key={log.id} onClick={() => setSelectedDiet(log)} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors" data-testid={`recent-diet-${log.id}`}>
                  <div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-green-500/10"><Apple className="h-4 w-4 text-green-500" /></div><div><p className="font-medium text-sm">{format(new Date(log.date), 'MMM d, yyyy')}</p><p className="text-xs text-muted-foreground">{t('userProfile.entriesCount', { count: log.entries?.length || 0 })}</p></div></div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-primary font-medium">{Math.round(log.total_calories)} kcal</span>
                    <span className="text-muted-foreground text-xs hidden sm:inline">P:{Math.round(log.total_protein)}g C:{Math.round(log.total_carbs)}g F:{Math.round(log.total_fat)}g</span>
                  </div>
                </div>
              ))}
              {recent_food_logs.length > 3 && (
                <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setShowAllDiet(!showAllDiet)}>
                  {showAllDiet ? <><ChevronUp className="h-4 w-4 mr-1" />{t('userProfile.showLess')}</> : <><ChevronDown className="h-4 w-4 mr-1" />{t('userProfile.showAll', { count: recent_food_logs.length })}</>}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/50">
        <CardHeader><CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('userProfile.sectionRecentCheckIns')}</CardTitle></CardHeader>
        <CardContent>
          {recent_measurements.length === 0 ? <p className="text-muted-foreground text-center py-6">{t('userProfile.noCheckIns')}</p> : (
            <div className="space-y-2">
              {measurementsToShow.map((m) => (
                <div key={m.id} onClick={() => setSelectedMeasurement(m)} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors" data-testid={`recent-measurement-${m.id}`}>
                  <div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-purple-500/10"><Scale className="h-4 w-4 text-purple-500" /></div><div><p className="font-medium text-sm">{format(new Date(m.date), 'MMM d, yyyy')}</p><div className="flex gap-3 text-xs text-muted-foreground"><span className="text-primary font-medium">{m.weight_kg} kg</span>{m.body_fat_percent && <span>{m.body_fat_percent}% BF</span>}{m.waist_cm && <span>{t('userProfile.measurement.waist_cm')}: {m.waist_cm}cm</span>}</div></div></div>
                </div>
              ))}
              {recent_measurements.length > 3 && (
                <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setShowAllMeasurements(!showAllMeasurements)}>
                  {showAllMeasurements ? <><ChevronUp className="h-4 w-4 mr-1" />{t('userProfile.showLess')}</> : <><ChevronDown className="h-4 w-4 mr-1" />{t('userProfile.showAll', { count: recent_measurements.length })}</>}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedWorkout} onOpenChange={() => setSelectedWorkout(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto p-0">
          {selectedWorkout && (
            <>
              {selectedWorkout.workout_photo && <PinchZoomImage src={selectedWorkout.workout_photo} alt={selectedWorkout.day_name} className="w-full aspect-video object-cover" />}
              <div className="p-6 space-y-4">
                <DialogHeader>
                  <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}><Dumbbell className="inline h-5 w-5 mr-2 text-primary" />{selectedWorkout.day_name}</DialogTitle>
                  <p className="text-sm text-muted-foreground">{format(new Date(selectedWorkout.completed_at), 'EEEE, MMM d yyyy')}</p>
                </DialogHeader>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-xl bg-muted/50"><Clock className="h-4 w-4 mx-auto text-blue-400 mb-1" /><p className="text-lg font-bold">{selectedWorkout.duration_minutes}m</p><p className="text-xs text-muted-foreground">{t('userProfile.statDuration')}</p></div>
                  <div className="text-center p-3 rounded-xl bg-muted/50"><Dumbbell className="h-4 w-4 mx-auto text-primary mb-1" /><p className="text-lg font-bold">{selectedWorkout.exercises?.length || selectedWorkout.exercises_count || 0}</p><p className="text-xs text-muted-foreground">{t('userProfile.statExercises')}</p></div>
                  <div className="text-center p-3 rounded-xl bg-muted/50"><Trophy className="h-4 w-4 mx-auto text-yellow-500 mb-1" /><p className="text-lg font-bold">{selectedWorkout.prs_achieved || 0}</p><p className="text-xs text-muted-foreground">{t('userProfile.statPRs')}</p></div>
                </div>
                {selectedWorkout.exercises && selectedWorkout.exercises.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('userProfile.dialogExercises')}</h4>
                    {selectedWorkout.exercises.map((ex, i) => (
                      <div key={i} className="flex items-center justify-between text-sm p-3 rounded-xl bg-muted/50">
                        <span className="font-medium truncate flex-1">{ex.exercise_name || ex.name}</span>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                          {ex.sets && <span>{ex.sets.length || ex.sets} {t('userProfile.sets')}</span>}
                          {ex.best_weight && <span className="text-primary font-medium">{ex.best_weight}kg</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedDiet} onOpenChange={() => setSelectedDiet(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto p-0">
          {selectedDiet && (
            <div className="p-6 space-y-4">
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}><Apple className="inline h-5 w-5 mr-2 text-green-500" />{t('userProfile.dialogDietLog')}</DialogTitle>
                <p className="text-sm text-muted-foreground">{format(new Date(selectedDiet.date), 'EEEE, MMM d yyyy')}</p>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="text-center p-3 rounded-xl bg-muted/50"><p className="text-xl font-bold text-primary">{Math.round(selectedDiet.total_calories)}</p><p className="text-xs text-muted-foreground">kcal</p></div>
                <div className="text-center p-3 rounded-xl bg-muted/50"><p className="text-xl font-bold text-red-400">{Math.round(selectedDiet.total_protein)}g</p><p className="text-xs text-muted-foreground">{t('userProfile.macroProtein')}</p></div>
                <div className="text-center p-3 rounded-xl bg-muted/50"><p className="text-xl font-bold text-amber-400">{Math.round(selectedDiet.total_carbs)}g</p><p className="text-xs text-muted-foreground">{t('userProfile.macroCarbs')}</p></div>
                <div className="text-center p-3 rounded-xl bg-muted/50"><p className="text-xl font-bold text-blue-400">{Math.round(selectedDiet.total_fat)}g</p><p className="text-xs text-muted-foreground">{t('userProfile.macroFat')}</p></div>
              </div>
              {selectedDiet.entries && selectedDiet.entries.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('userProfile.meals', { count: selectedDiet.entries.length })}</h4>
                  {selectedDiet.entries.map((entry, i) => (
                    <div key={i} className="p-3 rounded-xl bg-muted/50 space-y-1">
                      <div className="flex items-center justify-between"><span className="font-medium text-sm capitalize">{entry.meal_type || t('userProfile.meal', { number: i + 1 })}</span><span className="text-xs text-primary font-medium">{Math.round(entry.calories || 0)} kcal</span></div>
                      {entry.foods && entry.foods.map((food, j) => (<div key={j} className="flex items-center justify-between text-xs text-muted-foreground pl-2"><span className="truncate flex-1">{food.name}</span><span>{food.calories} kcal</span></div>))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedMeasurement} onOpenChange={() => setSelectedMeasurement(null)}>
        <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto p-0">
          {selectedMeasurement && (() => {
            const m = selectedMeasurement;
            const bodyMeasurements = Object.entries(MEASUREMENT_KEYS).filter(([key]) => m[key]).map(([key, i18nKey]) => ({ label: t(i18nKey), value: m[key] }));
            return (
              <div className="p-6 space-y-4">
                <DialogHeader>
                  <DialogTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}><Scale className="inline h-5 w-5 mr-2 text-purple-500" />{t('userProfile.dialogCheckIn')}</DialogTitle>
                  <p className="text-sm text-muted-foreground">{format(new Date(m.date), 'EEEE, MMM d yyyy')}</p>
                </DialogHeader>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-xl bg-muted/50"><Scale className="h-4 w-4 mx-auto text-primary mb-1" /><p className="text-xl font-bold">{m.weight_kg} kg</p><p className="text-xs text-muted-foreground">{t('userProfile.measureWeight')}</p></div>
                  {m.body_fat_percent && <div className="text-center p-3 rounded-xl bg-muted/50"><Flame className="h-4 w-4 mx-auto text-red-400 mb-1" /><p className="text-xl font-bold">{m.body_fat_percent}%</p><p className="text-xs text-muted-foreground">{t('userProfile.measureBodyFat')}</p></div>}
                  {m.sleep_hours && <div className="text-center p-3 rounded-xl bg-muted/50"><Clock className="h-4 w-4 mx-auto text-blue-400 mb-1" /><p className="text-xl font-bold">{m.sleep_hours}h</p><p className="text-xs text-muted-foreground">{t('userProfile.measureSleep')}</p></div>}
                </div>
                {(m.photo_front || m.photo_side || m.photo_back) && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[{src: m.photo_front, label: t('userProfile.photoFront')}, {src: m.photo_side, label: t('userProfile.photoSide')}, {src: m.photo_back, label: t('userProfile.photoBack')}].filter(p => p.src).map(p => (
                      <div key={p.label} className="relative"><PinchZoomImage src={p.src} alt={p.label} className="w-full aspect-[9/16] object-cover rounded-lg" /><span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">{p.label}</span></div>
                    ))}
                  </div>
                )}
                {bodyMeasurements.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Ruler className="h-4 w-4" /> {t('userProfile.measureBodyMeasurements')}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{bodyMeasurements.map(({ label, value }) => (<div key={label} className="flex items-center justify-between text-sm p-2 rounded-lg bg-muted/50"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value} cm</span></div>))}</div>
                  </div>
                )}
                {m.mood && <div className="text-center text-3xl">{{'great':'😄','good':'🙂','neutral':'😐','bad':'😟','terrible':'😣'}[m.mood] || m.mood}</div>}
                {m.notes && <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">{m.notes}</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
